import type { CataloguePath, SecurityPermission } from '@ic/security-registry';
import { toAccessRules, type EffectivePermissionsWire } from './accessRules';
import { evaluateAccess } from './evaluatorAdapter';

const hasRuleFields = (e: EffectivePermissionsWire): boolean =>
  e.denials !== undefined ||
  e.everyoneAllows !== undefined ||
  e.everyoneDenials !== undefined ||
  e.inactivatedPaths !== undefined ||
  e.reactivatedPaths !== undefined;

/**
 * Whether the signed-in user may use `permission`. Applies the same folder-inheritance rule as the
 * server (a folder grant covers its children, a folder Deny denies them). Callers pass registry
 * objects; this module never turns a string into a permission.
 */
export function canAccess(
  effective: EffectivePermissionsWire | null,
  permission: SecurityPermission | CataloguePath,
): boolean {
  if (!effective) return false;
  if (!hasRuleFields(effective)) {
    // Deploy skew (new SPA, backend that predates the rule sets): without denials or inactivations
    // the shared walk could show more than the server allows, so match explicit grants exactly.
    // Every current backend response carries the fields, so this is the older-backend window only.
    if (effective.isSystemAdmin) return true;
    const target = permission.toLowerCase();
    return effective.permissions.some((p) => p.code.toLowerCase() === target);
  }
  return evaluateAccess(toAccessRules(effective), permission);
}
